import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import type { DateRange } from '../../types/analytics';

interface Props {
  value: DateRange;
  onChange: (range: DateRange) => void;
  presets?: Array<{
    label: string;
    range: DateRange;
  }>;
}

export default function DateRangePicker({ value, onChange, presets }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const defaultPresets = [
    {
      label: 'Last 7 days',
      range: {
        start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      }
    },
    {
      label: 'Last 30 days',
      range: {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      }
    },
    {
      label: 'Last 90 days',
      range: {
        start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      }
    },
    {
      label: 'This year',
      range: {
        start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      }
    }
  ];

  const availablePresets = presets || defaultPresets;

  const formatDateRange = (range: DateRange) => {
    const start = new Date(range.start).toLocaleDateString();
    const end = new Date(range.end).toLocaleDateString();
    return `${start} - ${end}`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md hover:bg-gray-700/50 transition-colors"
      >
        <Calendar className="w-4 h-4" />
        <span>{formatDateRange(value)}</span>
        <ChevronDown className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-gray-800/95 backdrop-blur-sm border border-gray-700 rounded-lg shadow-xl z-50">
          <div className="p-4 space-y-4">
            {/* Presets */}
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Quick Select</h4>
              <div className="space-y-1">
                {availablePresets.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      onChange(preset.range);
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700/50 rounded-md transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Range */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Custom Range</h4>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={value.start}
                    onChange={(e) => onChange({ ...value, start: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">End Date</label>
                  <input
                    type="date"
                    value={value.end}
                    onChange={(e) => onChange({ ...value, end: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t border-gray-700">
              <button
                onClick={() => setIsOpen(false)}
                className="px-3 py-1 text-sm text-gray-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primary-600"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}